import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { matchConsolidationDetailRoutePath, QUERY_PARAMS } from '@futrli/shared-services';
import { PerformanceDashboardsView } from '@futrli/views/views/reports/PerformanceDashboardsView/PerformanceDashboardsView';
import { ReportDetailView } from '@futrli/views/views/reports/ReportDetailView/ReportDetailView';
import { useApiView } from '@futrli/views/hooks/useApiView';

/**
 * Hook that manages performance dashboards state for the dashboards tab.
 * Creates a PerformanceDashboardsView instance,
 * auto-selects the first dashboard on load, and creates a dedicated detail
 * view for the selected dashboard.
 */
export const usePerformanceDashboards = () => {
  const featureFlags = useFlags();
  const { workspaceId = '', organisationId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isConsolidation = !!matchConsolidationDetailRoutePath(window.location.pathname);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(null);

  // Check for dash-id in query parameters
  const queryParams = new URLSearchParams(location.search);
  const dashboardIdFromQuery = queryParams.get(QUERY_PARAMS.DASH_ID);

  const performanceDashboardsView = useApiView(
    useMemo(() => new PerformanceDashboardsView(workspaceId, organisationId), [workspaceId, organisationId])
  );

  const reportDetailView = useApiView(
    useMemo(
      () => new ReportDetailView(workspaceId, organisationId, selectedDashboardId, isConsolidation, featureFlags),
      [workspaceId, organisationId, selectedDashboardId, isConsolidation, featureFlags]
    )
  );

  const { performanceDashboards, isLoading } = performanceDashboardsView.state;
  const isSelectedDashboardAvailable = performanceDashboards.some(pd => pd.report_id === selectedDashboardId);

  const updateUrlDashboardId = useCallback(
    (dashboardId: string | null) => {
      const updatedQueryParams = new URLSearchParams(location.search);
      if (dashboardId) {
        updatedQueryParams.set(QUERY_PARAMS.DASH_ID, dashboardId);
      } else {
        updatedQueryParams.delete(QUERY_PARAMS.DASH_ID);
      }
      navigate({ search: updatedQueryParams.toString() }, { replace: true });
    },
    [location.search, navigate]
  );

  const handleDashboardChange = useCallback(
    (dashboardId: string | null) => {
      setSelectedDashboardId(dashboardId);
      updateUrlDashboardId(dashboardId);
    },
    [updateUrlDashboardId]
  );

  // Auto-select dashboard from query parameter or first dashboard when list loads
  useEffect(() => {
    if (!selectedDashboardId && performanceDashboards.length > 0) {
      // Prefer dashboard from query parameter if it exists in the list
      const dashboardToSelect =
        dashboardIdFromQuery && performanceDashboards.some(pd => pd.report_id === dashboardIdFromQuery)
          ? dashboardIdFromQuery
          : performanceDashboards[0].report_id;

      handleDashboardChange(dashboardToSelect);
    }
  }, [performanceDashboards, selectedDashboardId, dashboardIdFromQuery, updateUrlDashboardId, handleDashboardChange]);

  // If the selected dashboard is removed from the list, reset selection
  useEffect(() => {
    if (selectedDashboardId && !performanceDashboards.some(pd => pd.report_id === selectedDashboardId)) {
      handleDashboardChange(null);
    }
  }, [handleDashboardChange, performanceDashboards, selectedDashboardId]);

  return {
    dashboards: performanceDashboards,
    selectedDashboardId,
    selectedDashboardDetail: isSelectedDashboardAvailable ? reportDetailView.state.report : null,
    flags: reportDetailView.state.flags,
    isListLoading: isLoading,
    isDetailLoading: reportDetailView.state.isLoading,
    setSelectedDashboardId: handleDashboardChange
  };
};
